<script setup lang="ts">
/**
 * Diamonds to credits, in a modal off the title bar.
 *
 * This used to be a card on the Shop tab's Supplies shelf, sitting beside the
 * fuel refill and the top-tier footnote. Supplies is gone — fuel moved to the
 * merged Boosts pane, the footnote moved with it, and converting moved here,
 * off the credits readout in the title bar. It earns its own door rather than
 * a fourth rail category for one card: it is not a *thing sold*, it is a
 * currency exchange, and a shop rail entry that sells nothing changes reads
 * exactly like the "shop selling an invisible product" failure the liveries
 * tab was once pulled for.
 *
 * The credits figure in the title bar became the button that opens it, which
 * is the same move the rank chip already made for the ladder: the number a
 * player is looking at *is* the door to the screen that explains it, rather
 * than a sign pointing somewhere else.
 *
 * ── Why a modal and not a tab ────────────────────────────────────────────────
 * Same reasoning as `RankLadder.vue`: this is a thing you look up, act on and
 * close again, not a place to be. The pattern is copied rather than
 * reinvented — a `scrim` that declines on backdrop click, a `window` that
 * takes focus on mount and closes on Escape.
 *
 * ── The wording is `DiamondShop.convert()`'s, verbatim ───────────────────────
 * Converting is irreversible in a way no other spend in the game is: every
 * other purchase leaves the player holding a thing, and this leaves them
 * holding credits with **no route back to diamonds**. So the confirmation
 * states both sides, and the headline number is the diamonds — the headline
 * is always what is *paid*, and putting the credits received in the largest
 * text would be the diamond-pack dark pattern wearing a different hat. That
 * rule, and the exact copy it produced, is not repeated from memory here —
 * it is the same code that used to live in `DiamondShop.vue`, moved.
 */
import { computed, onMounted, ref, watch } from 'vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';

import type { Wallet } from '@/net/api';
import { askToBuy } from '@/state/confirm-purchase';
import { formatCredits } from './stat-display';

const props = defineProps<{
  wallet: Wallet;
  /** Whichever mutation is in flight garage-wide, so this disables in step. */
  busyId: string | null;
}>();

const emit = defineEmits<{
  close: [];
  /** The amount actually accepted, in diamonds. */
  convert: [diamonds: number];
}>();

const busy = computed(() => props.busyId !== null);

/**
 * How many diamonds the control will spend.
 *
 * Clamped to what the player actually has, so the button can never offer a
 * spend the server will refuse — same rule the old shop card followed.
 */
const amount = ref(Math.max(1, Math.min(1, props.wallet.diamonds)));

watch(
  () => props.wallet.diamonds,
  (held) => {
    if (held < 1) return;
    if (amount.value > held) amount.value = held;
    if (amount.value < 1) amount.value = 1;
  },
  { immediate: true },
);

const gain = computed(() => amount.value * props.wallet.creditsPerDiamond);

const dialog = ref<HTMLElement | null>(null);
onMounted(() => dialog.value?.focus());

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('close');
  }
}

/**
 * Converting is irreversible in a way no other spend here is.
 *
 * Every other purchase leaves the player holding a thing; this leaves them
 * holding credits and **no route back to diamonds**. So the confirmation
 * states both sides — what is spent and what arrives — and the headline
 * number is the diamonds, because the headline is always what is *paid*.
 * Putting the credits received in the largest text is the diamond-pack dark
 * pattern wearing a different hat.
 */
async function convert(): Promise<void> {
  const spend = Math.min(amount.value, props.wallet.diamonds);
  if (spend < 1) return;
  const accepted = await askToBuy({
    item: `${spend} diamonds to credits`,
    detail: `Pays out ${(spend * props.wallet.creditsPerDiamond).toLocaleString('en-GB')} credits · diamonds cannot be bought back`,
    price: spend,
    currency: 'diamonds',
    confirmLabel: `Convert ${spend} diamonds`,
  });
  if (accepted) emit('convert', spend);
}
</script>

<template>
  <div class="scrim" data-test="convert-credits" @click.self="emit('close')">
    <section
      ref="dialog"
      class="window"
      role="dialog"
      aria-modal="true"
      aria-label="Buy credits with diamonds"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header>
        <div>
          <h2>Buy credits with diamonds</h2>
          <p class="standing">
            <span class="value">{{ wallet.diamonds.toLocaleString('en-GB') }}</span>
            <CurrencyIcon kind="diamonds" /> held
            <span class="muted">·</span>
            <span class="value">{{ formatCredits(wallet.credits) }}</span>
            <CurrencyIcon kind="credits" /> held
          </p>
        </div>
        <button type="button" class="close" data-test="convert-close" @click="emit('close')">
          Close
        </button>
      </header>

      <p class="warn">
        Diamonds spent here do not come back. There is no route from credits to
        diamonds.
      </p>

      <div class="controls">
        <input
          type="range"
          class="slider"
          data-action="convert-amount"
          min="1"
          :max="Math.max(1, wallet.diamonds)"
          step="1"
          v-model.number="amount"
          :disabled="busy || wallet.diamonds < 1"
        />
        <label class="number">
          <input
            type="number"
            min="1"
            :max="Math.max(1, wallet.diamonds)"
            step="1"
            v-model.number="amount"
            :disabled="busy || wallet.diamonds < 1"
          />
          <span><CurrencyIcon kind="diamonds" :labelled="false" /></span>
        </label>
      </div>

      <p class="payout" data-test="convert-payout">
        <span class="value">{{ gain.toLocaleString('en-GB') }}</span> credits for
        {{ amount }} <CurrencyIcon kind="diamonds" />
      </p>

      <div class="actions">
        <button type="button" class="ghost" @click="emit('close')">Done</button>
        <button
          type="button"
          class="accept"
          data-action="convert-diamonds"
          :disabled="busy || wallet.diamonds < 1 || amount < 1"
          @click="convert()"
        >
          <template v-if="wallet.diamonds < 1">No diamonds</template>
          <template v-else>
            Convert {{ amount }} <CurrencyIcon kind="diamonds" :labelled="false" />
          </template>
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* Same scrim/window shape as `RankLadder.vue` — copied rather than shared, so
   this file reads on its own and a future edit to one does not silently ripple
   into the other. */
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(6, 8, 11, 0.72);
  z-index: 40;
}

.window {
  width: min(26rem, 100%);
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.1rem 1.2rem 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.5rem;
  background: #12151a;
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

h2 {
  margin: 0;
  font-size: 0.95rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.standing {
  margin: 0.3rem 0 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
}

.standing .value {
  font-family: ui-monospace, 'SF Mono', monospace;
  color: #fff;
}

.standing .muted {
  color: rgba(255, 255, 255, 0.3);
}

.close {
  flex: none;
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: none;
  color: var(--steel-400);
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.warn {
  margin: 0;
  padding: 0.55rem 0.7rem;
  border-left: 2px solid #d8a13a;
  border-radius: 2px;
  background: rgba(216, 161, 58, 0.09);
  font-size: 0.74rem;
  line-height: 1.45;
  color: var(--steel-200);
}

.controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.slider {
  flex: 1;
  accent-color: var(--accent);
}

.number {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex: none;
  font-size: 0.72rem;
  color: var(--steel-400);
}

.number input {
  width: 6rem;
  padding: 0.35rem 0.45rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-900);
  color: #fff;
  font-family: ui-monospace, 'SF Mono', monospace;
}

.payout {
  margin: 0;
  font-size: 0.85rem;
  color: var(--steel-200);
}

.payout .value {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 1.1rem;
  color: #9fd7f5;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.ghost {
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font-size: 0.82rem;
}

.accept {
  padding: 0.5rem 1.1rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: var(--accent);
  color: #14181d;
  font-size: 0.82rem;
  font-weight: 700;
}

.accept:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
